import { GridLayout, GridItemDimensions, GridState, GriddedItem } from "./grid";

class ItemLayoutVariant {
  private _width: number;
  private _height: number;
  private _marginX: number;
  private _marginY: number;

  constructor(width: number, height: number, marginX: number, marginY: number) {
    this._width = width;
    this._height = height;
    this._marginX = marginX;
    this._marginY = marginY;
    return this;
  }

  width(): number {
    return this._width;
  }

  outerWidth(): number {
    return this._width + this._marginX;
  }

  height(): number {
    return this._height;
  }

  outerHeight(): number {
    return this._height + this._marginY;
  }

  box(): GridItemDimensions {
    return {
      width: this._width,
      height: this._height
    };
  }
}

type FlowItemData = {
  DOM: HTMLElement,
  width?: number,
  height?: number,
  type: string,
  index: number,
  css: {
    top?: number,
    left?: number,
    width?: number,
    height?: number
  }
}

export default function flowGridder(items: HTMLElement[], state: GridState): GridLayout {
  let marginX = state.gridSpacingXPx ?? 0;
  let marginY = state.gridSpacingXPx ?? 0;
  let width = state.containerWidth;
  let allpri = state.allpri ?? false;

  // Find projects with portrait-mode thumbs
  let flowItemList: FlowItemData[] = [];
  for (let i=0; i<items.length; i++) {
    let item = items[i];
    let itemData: FlowItemData = {
      DOM: item,
      index: flowItemList.length,
      type: '',
      css: {}
    };
    let classList = item.classList;

    if (classList.contains('portrait')) {
      if (classList.contains('full')) {
        itemData.type = 'Y';
      } else {
        itemData.type = 'P';
      }
    }
    
    else {
      if (classList.contains('full')) {
        itemData.type = 'X';
      } else if (allpri || classList.contains('pri')) {
        itemData.type = 'L';
      } else {
        itemData.type = 'l';
      }
    }
    flowItemList.push(itemData);
  }


  // Calculate imagetype sizes
  let landscapeRatio = 16/9;
  let landscape1width = (width - marginX - marginX) / 3;
  let landscape1height = landscape1width / landscapeRatio;
  let landscape2width = (width - marginX) / 2; // Half minus half margin
  let landscape2height = landscape2width / landscapeRatio;
  let landscape3width = landscape1width + marginX + landscape1width;
  let landscape3height = landscape1height + marginY + landscape1height;

  let portrait1width = landscape1width; // One third
  let portrait1height = landscape3height;
  let portrait2width = landscape2width; // Half
  let portrait2height = landscape2height + marginY + landscape2height;

  let landscapeFullWidth = width;
  let landscapeFullHeight = width / landscapeRatio;
  let portraitFullWidth = width;
  let portraitFullHeight = width / (portrait1width/portrait1height);

  let L0 = new ItemLayoutVariant(landscape1width, landscape1height, marginX, marginY);
  let L1 = new ItemLayoutVariant(landscape2width, landscape2height, marginX, marginY);
  let L2 = new ItemLayoutVariant(landscape3width, landscape3height, marginX, marginY);
  let P1 = new ItemLayoutVariant(portrait1width, portrait1height, marginX, marginY);
  let P2 = new ItemLayoutVariant(portrait2width, portrait2height, marginX, marginY);
  let FL = new ItemLayoutVariant(landscapeFullWidth, landscapeFullHeight, marginX, marginY);
  let FP = new ItemLayoutVariant(portraitFullWidth, portraitFullHeight, marginX, marginY);
  
  // Set initial values
  let pos = {
    top: 0,
    left: 0
  };

  // Helper functions
  function d(image: FlowItemData, x: number, y: number, size: GridItemDimensions) {
    let top = pos.top;
    let left = pos.left;
    image.css.left = left + (x || 0);
    image.css.top = top + (y || 0);
    image.css.width = size.width;
    image.css.height = size.height;
  };
  
  function averageIndex(list: FlowItemData[]) {
    let sum = 0;
    for (let i=0; i<list.length; i++) {
      sum += list[i].index;
    }
    return sum/list.length;
  }

  let drawLayouts = {
    LLPPlll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      d(L.shift(), L0.outerWidth(), 0, L2.box());
      d(P.shift(), 0, L0.outerHeight(), P1.box());
      d(l.shift(), P1.outerWidth(), L2.outerHeight(), L0.box());
      d(P.shift(), P1.outerWidth() + L0.outerWidth(), L2.outerHeight(), P1.box());
      d(L.shift(), 0, L0.outerHeight() + P1.outerHeight(), L2.box());
      d(l.shift(), L2.outerWidth(), L2.outerHeight() + P1.outerHeight(), L0.box());
      return L2.outerHeight() + P1.outerHeight() + L0.outerHeight();
    },
    LLPll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      d(L.shift(), L0.outerWidth(), 0, L2.box());
      d(P.shift(), 0, L0.outerHeight(), P1.box());
      d(L.shift(), L0.outerWidth(), L2.outerHeight(), L2.box());
      d(l.shift(), 0, L0.outerHeight() + P1.outerHeight(), L0.box());
      return L2.outerHeight() + L2.outerHeight();	
    },
    LPPllll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      d(L.shift(), L0.outerWidth(), 0, L2.box());
      d(P.shift(), 0, L0.outerHeight(), P1.box());
      d(l.shift(), P1.outerWidth(), L2.outerHeight(), L0.box());
      d(P.shift(), P1.outerWidth() + L0.outerWidth(), L2.outerHeight(), P1.box());
      d(l.shift(), 0, L0.outerHeight() + P1.outerHeight(), L0.box());
      d(l.shift(), L0.outerWidth(), L2.outerHeight() + L0.outerHeight(), L0.box());
      return L2.outerHeight() + P1.outerHeight();
    },
    LPlll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      d(L.shift(), L0.outerWidth(), 0, L2.box());
      d(P.shift(), 0, L0.outerHeight(), P1.box());
      d(l.shift(), P1.outerWidth(), L2.outerHeight(), L0.box());
      d(l.shift(), P1.outerWidth() + L0.outerWidth(), L2.outerHeight(), L0.box());
      return L0.outerHeight() + P1.outerHeight();
    },
    PPlllll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      if (averageIndex(l) > averageIndex(P)) {
        d(l.shift(), 0, 0, L0.box());
        d(l.shift(), L0.outerWidth(), 0, L2.box());
        d(P.shift(), 0, L0.outerHeight(), P1.box());
        d(l.shift(), P1.outerWidth(), L2.outerHeight(), L0.box());
        d(P.shift(), P1.outerWidth() + L0.outerWidth(), L2.outerHeight(), P1.box());
        d(l.shift(), 0, L0.outerHeight() + P1.outerHeight(), L0.box());
        d(l.shift(), L0.outerWidth(), L2.outerHeight() + L0.outerHeight(), L0.box());
      } else {
        d(l.shift(), 0, 0, L2.box());
        d(l.shift(), L2.outerWidth(), 0, L0.box());
        d(P.shift(), L2.outerWidth(), L0.outerHeight(), P1.box());
        d(P.shift(), 0, L2.outerHeight(), P1.box());
        d(l.shift(), P1.outerWidth(), L2.outerHeight(), L0.box());
        d(l.shift(), P1.outerWidth(), L2.outerHeight() + L0.outerHeight(), L0.box());
        d(l.shift(), P1.outerWidth() + L0.outerWidth(), L2.outerHeight() + L0.outerHeight(), L0.box());
      }
      return L2.outerHeight() + P1.outerHeight();
    },
    Pllll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      if (l[0].index > P[0].index) {
        d(P.shift(), 0, 0, P1.box());
        d(l.shift(), P1.outerWidth(), 0, L0.box());
        d(l.shift(), P1.outerWidth() + L0.outerWidth(), 0, L0.box());
        d(l.shift(), P1.outerWidth(), L0.outerHeight(), L0.box());
        d(l.shift(), P1.outerWidth() + L0.outerWidth(), L0.outerHeight(), L0.box());
      } else if (l[2].index > P[0].index) {
        d(l.shift(), 0, 0, L0.box());
        d(l.shift(), 0, L0.outerHeight(), L0.box());
        d(P.shift(), L0.outerWidth(), 0, P1.box());
        d(l.shift(), L0.outerWidth() + P1.outerWidth(), 0, L0.box());
        d(l.shift(), L0.outerWidth() + P1.outerWidth(), L0.outerHeight(), L0.box());
      } else {
        d(l.shift(), 0, 0, L0.box());
        d(l.shift(), 0, L0.outerHeight(), L0.box());
        d(l.shift(), L0.outerWidth(), 0, L0.box());
        d(l.shift(), L0.outerWidth(), L0.outerHeight(), L0.box());
        d(P.shift(), L0.outerWidth() + L0.outerWidth(), 0, P1.box());
      }
      return P1.outerHeight();
    },
    PPPlll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P1.box());
      d(l.shift(), P1.outerWidth(), 0, L0.box());
      d(P.shift(), P1.outerWidth() + L0.outerWidth(), 0, P1.box());
      d(l.shift(), 0, P1.outerHeight(), L0.box());
      d(P.shift(), P1.outerWidth(), L0.outerHeight(), P1.box());
      d(l.shift(), P1.outerWidth() + P1.outerWidth(), P1.outerHeight(), L0.box());
      return P1.outerHeight() + L0.outerHeight();
    },
    PPll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P1.box());
      d(l.shift(), P1.outerWidth(), 0, L0.box());
      d(l.shift(), P1.outerWidth(), L0.outerHeight(), L0.box());
      d(P.shift(), P1.outerWidth() + L0.outerWidth(), 0, P1.box());
      return P1.outerHeight();
    },
    PLL: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P2.box());
      d(L.shift(), P2.outerWidth(), 0, L1.box());
      d(L.shift(), P2.outerWidth(), L1.outerHeight(), L1.box());
      return P2.outerHeight();
    },
    PLl: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P2.box());
      d(l.shift(), P2.outerWidth(), 0, L1.box());
      d(l.shift(), P2.outerWidth(), L1.outerHeight(), L1.box());
      return P2.outerHeight();
    },
    Pll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P2.box());
      d(l.shift(), P2.outerWidth(), 0, L1.box());
      d(l.shift(), P2.outerWidth(), L1.outerHeight(), L1.box());
      return P2.outerHeight();
    },
    LLP: function(L: FlowItemData[], P: FlowItemData[], l?: FlowItemData[]) {
      d(L.shift(), 0, 0, L1.box());
      d(L.shift(), 0, L1.outerHeight(), L1.box());
      d(P.shift(), L1.outerWidth(), 0, P2.box());
      return P2.outerHeight();
    },
    lPl: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      return drawLayouts.LLP(l, P);	// NOTE: swapped l / L
    },
    llP: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      return drawLayouts.LLP(l, P); // NOTE: Swapped l/L
    },
    Lll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      if (averageIndex(l) > averageIndex(L)) {
        d(L.shift(), 0, 0, L2.box());
        d(l.shift(), L2.outerWidth(), 0, L0.box());
        d(l.shift(), L2.outerWidth(), L0.outerHeight(), L0.box());
      } else {
        d(l.shift(), 0, 0, L0.box());
        d(l.shift(), 0, L0.outerHeight(), L0.box());
        d(L.shift(), L0.outerWidth(), 0, L2.box());
      }
      return L2.outerHeight();
    },
    LLlX: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      return drawLayouts.Lll([L[0]], P, [L[1], l[0]]);
    },
    LP: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      var iL = L.shift();
      var iP = P.shift();
      if (iL.index < iP.index) {
        d(iL, 0, 0, L2.box());
        d(iP, L2.outerWidth(), 0, P1.box());
      } else {
        d(iP, 0, 0, P1.box());
        d(iL, P1.outerWidth(), 0, L2.box());
      }
      return P1.outerHeight();
    },
    Pl: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      return drawLayouts.LP(l, P, L);	// NOTE: Swapped l/L
    },
    PPP: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P1.box());
      d(P.shift(), P1.outerWidth(), 0, P1.box());
      d(P.shift(), P1.outerWidth() + P1.outerWidth(), 0, P1.box());
      return P1.outerHeight();
    },
    llllll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      var singleHeight = L2.outerHeight();
      d(l.shift(), 0, 0, L2.box());
      d(l.shift(), L2.outerWidth(), 0, L0.box());
      d(l.shift(), L2.outerWidth(), L0.outerHeight(), L0.box());

      d(l.shift(), 0, singleHeight, L0.box());
      d(l.shift(), 0, singleHeight + L0.outerHeight(), L0.box());
      d(l.shift(), L0.outerWidth(), singleHeight, L2.box());

      return 2 * singleHeight;
    },
    lll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      d(l.shift(), L0.outerWidth(), 0, L0.box());
      d(l.shift(), L0.outerWidth() + L0.outerWidth(), 0, L0.box());
      return L0.outerHeight();
    },
    LL: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(L.shift(), 0, 0, L1.box());
      d(L.shift(), L1.outerWidth(), 0, L1.box());
      return L1.outerHeight();
    },
    Ll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      if (averageIndex(L) < averageIndex(l)) {
        d(L.shift(), 0, 0, L1.box());
        d(l.shift(), L1.outerWidth(), 0, L1.box());
      } else {
        d(l.shift(), 0, 0, L1.box());
        d(L.shift(), L1.outerWidth(), 0, L1.box());
      }
      return L1.outerHeight();
    },
    PP: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P2.box());
      d(P.shift(), P2.outerWidth(), 0, P2.box());
      return P2.outerHeight();
    },
    ll: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L1.box());
      d(l.shift(), L1.outerWidth(), 0, L1.box());
      return L1.outerHeight();
    },
    l: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(l.shift(), 0, 0, L0.box());
      return L0.outerHeight();
    },
    L: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(L.shift(), 0, 0, L2.box());
      return L2.outerHeight();
    },
    P: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[]) {
      d(P.shift(), 0, 0, P1.box());
      return P1.outerHeight();
    },
    Y: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[], X: FlowItemData[], Y: FlowItemData[]) {
      d(Y.shift(), 0, 0, FP.box());
      return FP.outerHeight();
    },
    X: function(L: FlowItemData[], P: FlowItemData[], l: FlowItemData[], X: FlowItemData[], Y: FlowItemData[]) {
      d(X.shift(), 0, 0, FL.box());
      return FL.outerHeight();
    }
  };
  
  // Get all presets
  let presets = Object.keys(drawLayouts);
  presets.sort(function(a, b) {
    return b.length-a.length;
  });

  function getTypeString(types, sort?) {
    let str = '';
    for (let i=0; i<types._.length; i++) {
      str += types._[i].type;
    }
    if (allpri) {
      str = str.toUpperCase();
    }
    if (sort) {
      return str.split('').sort().join('');
    } else {
      return str;
    }
  };

  let maxLength = presets[0].length;
  let currentPos = 0;
  while (currentPos < flowItemList.length) {
    let types = {
      L: [],
      P: [],
      l: [],
      X: [],
      Y: [],
      S: [],
      _: []
    };
    let targetPos = currentPos+maxLength;
    
    // Start by adding up to maxPos items
    for (; currentPos<targetPos && currentPos < flowItemList.length; currentPos++) {
      let image = flowItemList[currentPos];
      types._.push(image);
      types[allpri ? image.type.toUpperCase() : image.type].push(image);			
    }
    
    // Remove one item at a time from the end, when there is no matching draw function
    let typeString: string;
    while (
      (currentPos === (flowItemList.length-1) && types._.length > 2) || // Avoid getting a single last element when possible
      (
        !drawLayouts[(typeString = getTypeString(types))] && 
        !drawLayouts[(typeString = getTypeString(types, true))] && 
        !(currentPos === flowItemList.length && drawLayouts[typeString += 'X']) // Add an X if we're at the end of the list
      )
    ) {
      let image = types._.pop();
      types[allpri ? image.type.toUpperCase() : image.type].pop();
      currentPos--;
    }
    
    // Call draw
    // Peeky.debug('Draw: ' + typeString);
    pos.top += drawLayouts[typeString](types.L, types.P, types.l, types.X, types.Y, types.S);
  }

  let griddedItemList: GriddedItem[] = flowItemList.map((item) => {
    return {
      DOM: item.DOM,
      css: item.css
    }
  });

  return {
    itemList: griddedItemList,
    height: Math.max(0, pos.top - marginY)
  };
};

